import { MenuHorizontalService } from './../../../core/_base/layout/services/menu-horizontal.service';
// Angular
import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
// RxJS
import { Observable, Subscription } from 'rxjs';
// Object-Path
import * as objectPath from 'object-path';
// Layout
import {
  LayoutConfigService,
  MenuConfigService,
  PageConfigService,
} from '../../../core/_base/layout';
import { HtmlClassService } from '../html-class.service';
import { LayoutConfig } from '../../../core/_config/layout.config';
import { MenuConfig } from '../../../core/_config/menu.config';
import { PageConfig } from '../../../core/_config/page.config';
// User permissions
import { NgxPermissionsService } from 'ngx-permissions';
import { currentUserPermissions, Permission } from '../../../core/auth';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../core/reducers';
import { Router, NavigationEnd } from '@angular/router';
import { TrackingService } from '@app/api/tracking.service';
import { filter } from 'rxjs/operators';
import { SignalRService } from '@app/services/signalr.service';
import { isLoggedIn } from '@app/core/auth';
import { environment } from '@app/../environments/environment';

@Component({
  selector: 'kt-base',
  templateUrl: './base.component.html',
  styleUrls: ['./base.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BaseComponent implements OnInit, OnDestroy {
  private currentTrackingId: string | null = null;
  @Input() showLeft: boolean = false;
  isAskFormOpen: boolean = false;
  enableSnowEffect: boolean = false; // Tắt hiệu ứng rơi tuyết
  isWidgetsOpen: boolean = false;
  // Public variables
  selfLayout: string;
  asideDisplay: boolean;
  asideSecondary: boolean;
  subheaderDisplay: boolean;
  fluid: boolean;
  isAdminRoute: boolean = false;

  // Private properties
  private unsubscribe: Subscription[] = [];
  private currentUserPermissions$: Observable<Permission[]>;

  /**
   * Component constructor
   *
   * @param layoutConfigService: LayoutConfigService
   * @param menuConfigService: MenuConfifService
   * @param pageConfigService: PageConfigService
   * @param htmlClassService: HtmlClassService
   * @param store
   * @param permissionsService
   */
  constructor(
    private layoutConfigService: LayoutConfigService,
    private menuConfigService: MenuConfigService,
    private pageConfigService: PageConfigService,
    private htmlClassService: HtmlClassService,
    private store: Store<AppState>,
    private permissionsService: NgxPermissionsService,
    private menuHorizontalService: MenuHorizontalService,
    private trackingService: TrackingService,
    private router: Router,
    private signalRService: SignalRService
  ) {
    this.loadRolesWithPermissions();

    // register configs by demos
    this.layoutConfigService.loadConfigs(new LayoutConfig().configs);
    this.menuConfigService.loadConfigs(new MenuConfig().configs);
    this.pageConfigService.loadConfigs(new PageConfig().configs);

    // setup element classes
    this.htmlClassService.setConfig(this.layoutConfigService.getConfig());

    const subscr = this.layoutConfigService.onConfigUpdated$.subscribe((layoutConfig) => {
      // reset body class based on global and page level layout config, refer to html-class.service.ts
      document.body.className = '';
      this.htmlClassService.setConfig(layoutConfig);
    });
    this.unsubscribe.push(subscr);

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // 1. Kết thúc track cũ nếu có
        this.closePreviousTracking();

        // 2. Kiểm tra URL (Khớp với logic /product/ID/details của bạn)
        if (event.url.includes('/product/') && event.url.includes('/details')) {
          const parts = event.url.split('/');
          const productIndex = parts.indexOf('product');

          if (productIndex !== -1 && parts[productIndex + 1]) {
            const productId = parts[productIndex + 1];

            this.trackProductView(productId);
          }
        }

        // 3. Kiểm tra xem có đang ở trang Admin không (chỉ check URL)
        this.isAdminRoute = event.urlAfterRedirects ? event.urlAfterRedirects.startsWith('/admin') : event.url.startsWith('/admin');
      });
  }

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */

  ngOnInit(): void {
    this.menuHorizontalService.loadMenu();
    const config = this.layoutConfigService.getConfig();
    this.selfLayout = objectPath.get(config, 'self.layout');
    this.asideDisplay = objectPath.get(config, 'aside.self.display');
    this.subheaderDisplay = objectPath.get(config, 'subheader.display');
    this.fluid = objectPath.get(config, 'content.width') === 'fluid';

    // let the layout type change
    const subscr = this.layoutConfigService.onConfigUpdated$.subscribe((cfg) => {
      setTimeout(() => {
        this.selfLayout = objectPath.get(cfg, 'self.layout');
      });
    });
    this.unsubscribe.push(subscr);

    // Initial check for Admin route on page load
    this.isAdminRoute = this.router.url.startsWith('/admin');

    // Initialize SignalR to receive real-time notifications (only when logged in)
    this.startSignalR();
  }

  /** Initialize a SignalR connection to receive real-time notifications */
  private startSignalR() {
    const token = localStorage.getItem(environment.authTokenKey);
    if (token && token.length > 10) {
      this.signalRService.start();
    }
    const loginSub = this.store.pipe(select(isLoggedIn)).subscribe((loggedIn) => {
      if (loggedIn) {
        this.signalRService.start();
      } else {
        this.signalRService.stop();
      }
    });
    this.unsubscribe.push(loginSub);
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }

  /*
   * NGX Permissions, init roles
   */
  loadRolesWithPermissions() {
    this.currentUserPermissions$ = this.store.pipe(select(currentUserPermissions));
    const subscr = this.currentUserPermissions$.subscribe((res) => {
      if (!res || res.length === 0) {
        return;
      }

      this.permissionsService.flushPermissions();
      res.forEach((pm: Permission) => this.permissionsService.addPermission(pm.name));
    });
    this.unsubscribe.push(subscr);
  }
  toggleAskForm() {
    this.isAskFormOpen = !this.isAskFormOpen;
    if (this.isAskFormOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }
  trackProductView(productId: string) {
    const deviceType = window.innerWidth <= 768 ? 'Mobile' : 'Desktop';

    this.trackingService.apiProductViewTrackPost(productId, deviceType).subscribe(
      (res) => {
        if (res && res.success) {
          this.currentTrackingId = res.data.tracking_id;
        }
      },
      (err) => console.error('Lỗi khi track:', err)
    );
  }
  private closePreviousTracking() {
    if (this.currentTrackingId) {
      this.trackingService.apiProductViewUpdateEndPut(this.currentTrackingId).subscribe(() => {
        this.currentTrackingId = null;
      });
    }
  }
  @HostListener('window:beforeunload')
  confirmExit() {
    this.closePreviousTracking();
  }
}
